/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  isJobElementTotal,
  JobElementTotal,
  JobElementType,
  jobSpecCreateDialogRoute,
  jobCsCreateDialogRoute,
  isJobEditable,
  FilterNames,
  FilterRestService,
} from '@gh/builder-shared';
import { BuilderPermission, BuilderPermissionSet, JobStatus } from '@gh/builder-shared-data';
import {
  AppRouter, DateTimeTypes,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData, NumberTypes,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  buildNumberColumnLeft,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import {
  conditionalRowPostCreateDecorator, GridSettingsService,
  textOverlayRowPostCreate,
} from '@gh/common-internal-shared';
import { DrawListFilter, DrawListItem } from '../../resources/draw-list.model';
import { DrawListBusinessService } from '../../resources/draw-list-business.service';
import { filter$ } from '@gh/rx/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslationService } from '@gh/core-mls';

@Component({
  standalone: false,
             selector: 'gh-draw-list',
             templateUrl: 'draw-list.component.html',
             styleUrls: ['draw-list.component.scss'],
           })
export class DrawListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<DrawListItem>;
  form: FilterableFormState<DrawListFilter>;
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  total$: Observable<any>;
  _total  = new BehaviorSubject<JobElementTotal[]>([{type: JobElementType.Total, id: 'total-count'}]);
  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: UserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: DrawListBusinessService,
              private notifications: NotificationService,
              private router: AppRouter,
              private filterService: FilterRestService,
              private translations: TranslationService,
              private navigationService: BuilderNavigationService,
              private dialogService: DialogService,
              private gridService: GridSettingsService,
              public gridDefinitionFactory: GridDefinitionFactory) {
    this.total$ = this._total.asObservable();
    this.gridService.setCurrentTable('drawList');
  }

  isJobDpp(job: DrawListItem): boolean {
    return job.isDpp;
  }

  ngOnInit(): void {
    const {filter: initFilter, gridLayout} = getRouteInitData(this.route);
    let isInit = true;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        jobNumber: {
          // list always gets sorted from BE
          comparator: () => 0,
        },
        contractAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        variationsAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        totalAmount: buildNumberColumnLeft(NumberTypes.Price),
        totalAmountWithGst: buildNumberColumnLeft(NumberTypes.Price),
        estimatedProfit: buildNumberColumnLeft(NumberTypes.Price),
        profitMargin: buildNumberColumnLeft(NumberTypes.Price),
        drawsMadeWithGst: buildNumberColumnLeft(NumberTypes.Price),
        actualProfit: buildNumberColumnLeft(NumberTypes.Price),
        balanceToReceive: buildNumberColumnLeft(NumberTypes.Price),
        status: {
          minWidth: 120,
          cellClass: ({ data }: { data: any }) => ({
            [JobStatus.Dpp]: 'cell--important',
            [JobStatus.NotProceeding]: 'cell--negative',
            [JobStatus.Active]: 'cell--positive',
            [JobStatus.Finalized]: 'finalized-status',
          }[data.statusValue]),
        },
        finalizedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        lastAccountingDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        finalClaimDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        handoverDate: buildDateTimeColumn(DateTimeTypes.ShortDate),

      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        getRowId: (params) => String(params.data.id),
        processRowPostCreate: conditionalRowPostCreateDecorator(
          ({ node: { data } }) => isJobElementTotal(data),
          textOverlayRowPostCreate(this.translations.get('lbl_total').toUpperCase())),
      },
    );
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showFinalized: void 0,
        showActive: void 0,
        showNotProceeding: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.dataSource = this.businessService.findAllAsDataSource({
                                                                   ...filter,
                                                                 });
      if (!isInit) {
        this.filterService.update(FilterNames.drawList, filter).subscribe();
      }
      isInit = false;
    });
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  onJobDetailsOpen(job: DrawListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobDetails.ANY)) {
      if (job.isDpp) {
        this.router.navigateToChild(['/job-admin/jobs/dpp', job.id], {
          parentTitle: 'lbl_draws',
        });
      } else {
        this.router.navigateToChild(['/job-admin/jobs', job.id], {
          parentTitle: 'lbl_draws',
        });
      }
    }
  }

  goToAel(job?: DrawListItem): void {
    this.navigationService.goToAel({
       label: 'lbl_draws',
       entityType: 'JOB',
       entityId: job ? job.id : undefined,
     });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onJobEstimateOpen(job: DrawListItem): void {
    this.router.navigateToChild(['job-admin/jobs', job.id, 'estimate'], {
      parentTitle: 'lbl_draws',
    });
  }

  onFinancialsOpen(job: DrawListItem): void {
    this.router.navigateToChild(['job-admin', 'job-financials', job.id], {
      parentTitle: 'lbl_draws',
    });
  }

  onJobScheduleOpen(): void {
    // not implemented yet
  }

  onJobOrderOpen(job: DrawListItem): void {
    this.router.navigateToChild(['/job-admin/job-orders', job.id], {
      parentTitle: 'lbl_draws',
    });
  }

  onJobChangeOrderOpen(job: DrawListItem): void {
    this.router.navigateToChild([`/job-admin/job-change-orders`, job.id], {
      parentTitle: 'lbl_draws',
    });
  }

  onMakeClaim(job: DrawListItem): void {
    if (!this.isElement(job)) {
      return;
    }
    this.router.navigateToChild([`/accounting/draws`, job.id], {
      parentTitle: 'lbl_draws',
    });
  }

  onColorSelectionOpen(job: DrawListItem): void {
    // this.jobNavigation.onColorSelectionOpen(job, 'lbl_draws');
    if (job.isCsExist) {
      this.router.navigateToChild(['job-admin/jobs', job.id, 'color-selection'], { parentTitle: 'lbl_draws' });
    } else if (!isJobEditable(job)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT)) {
      this.notifications.error('msg_cs_empty');
    } else {
      this.dialogService.openWithInput(jobCsCreateDialogRoute, { isFromCreateBtn: false })
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((csm: any) =>
       this.router.navigateToChild(
         ['job-admin/jobs', job.id, 'color-selection', 'new'],
         {
           queryParams: { csMasterId: csm.master.id },
           parentTitle: 'lbl_draws',
         }));
    }
  }

  onJobSpecificationOpen(job: DrawListItem): void {
    if (job.isSpecExist) {
      this.router.navigateToChild([`/job-admin/jobs/${job.id}/spec`], {parentTitle: 'lbl_draws'});
    } else if (!isJobEditable(job)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)) {
      this.notifications.error('msg_spec_empty');
    } else {
      this.dialogService.openWithInput(jobSpecCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((formValue: any) => {
          this.router.navigateToChild(
            [`/job-admin/jobs/${job.id}/spec/new`],
            {
              queryParams: {specTemplateId: formValue.jobSpecTemplate.id},
              parentTitle: 'lbl_draws',
            });
        });
    }
  }
  onLoadData(event: any): void {
    const totals = event.totals;
    const total = {};
    for (const key in totals) {
      if (Object.prototype.hasOwnProperty.call(totals, key)) {
        total[key] = totals[key];
      }
    }
    this._total.next([{type: JobElementType.Total, id: 'total-count', variationId: 'variation:footer', ...total  }]);
  }

  isElement(element: any): boolean {
    return element.type !== JobElementType.Total;
  }
}
